import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface NumericInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  unit?: string;  // Optional parameter for unit of measure
  placeholder?: string;
  allowZero?: boolean; // New option for allowing zero
  error?: string; // Optional error message to display
}

const NumericInput: React.FC<NumericInputProps> = ({ 
  label, 
  value, 
  onChange,
  placeholder = "Min",
  allowZero = true, // Default true for compatibility with existing code
  error: externalError // Renamed to avoid conflict with internal error state
}) => {
  const { t } = useTranslation();
  const [internalError, setInternalError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (!allowZero && newValue === '0') {
      setInternalError('Error: Min must be greater than 0.');
    } else {
      setInternalError('');
    }
    onChange(String(newValue));
  };

  // Translate error if it's a translation key, otherwise show as-is
  const displayError = externalError || internalError;
  const translatedError = displayError ? t(displayError, { defaultValue: displayError }) : '';

  return (
    <div className="mb-3 sm:mb-4">
      <label className="block text-foreground font-medium mb-1 sm:mb-2 text-xs sm:text-sm md:text-base">
        {label}
      </label>
      <input
        type="number"
        min={allowZero ? "0" : "1"}
        value={value}
        onChange={handleChange}
        className="w-full px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-md text-xs sm:text-sm md:text-base transition-all duration-200 focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 focus:outline-none hover:border-yellow-400"
        placeholder={placeholder}
      />
      {translatedError && (
        <div className="text-red-500 text-xs sm:text-sm mt-1">{translatedError}</div>
      )}
    </div>
  );
};

export default NumericInput;
